import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useTranslation } from "react-i18next";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { SaveIcon } from "../../../utils/icons/icons";
import MissionFormContent from "./MissionFormContent";
import ReadOnlyProtectedButton from "../../Inputs/ReadOnlyProtectedButton";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import { selectMissionsRestSet, selectSelectedRoomMissions } from "../../../store/room/room-selectors";
import Mission, {
  getDefaultMissionName,
  getNextColorForMission,
  isDefaultMissionNameStructure,
} from "../../../interfaces/robot/missions/mission";
import { NavigatorName } from "../../../enums/command-hub/navigator";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import DialogWrapperWithClose from "../../../components/General/DialogWrapperWithClose";
import { validateName } from "../../../validation/mission-validation";

const MissionForm: React.FC<{
  open: boolean;
  onClose: () => void;
  mission: Partial<Mission>;
  roomId?: string;
  navigatorAsName?: boolean;
}> = ({ open, onClose, mission, roomId, navigatorAsName }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { patchMission, postNewMission } = useSharedActions();

  const roomMissions = useSharedSelector((state) => selectSelectedRoomMissions(state));
  const missionsRestSet = useSharedSelector((state) => selectMissionsRestSet(state));

  const [formMission, setFormMission] = useState<Mission>({
    color: getNextColorForMission(roomMissions.length),
    repeat: 1,
    fixedOrder: false,
    doWallFollow: true,
    selectedLandmarkIds: [],
    selectedZoneIds: [],
    name: getDefaultMissionName(NavigatorName.ZoneCleaning, roomMissions, 1),
    room: roomId ?? null,
    id: uuidv4(),
    mode: NavigatorName.ZoneCleaning,
    ...mission,
  });

  useEffect(() => {
    if (
      (missionsRestSet.patchStatus === HttpFetchStatus.Success ||
        missionsRestSet.postStatus === HttpFetchStatus.Success) &&
      open
    ) {
      onClose();
    }
  }, [missionsRestSet.patchStatus, missionsRestSet.postStatus, onClose, open]);

  const closeDialogHandler = () => {
    onClose();
  };

  const updateMissionHandler = (updatedMission: Partial<Mission>) => {
    if (isDefaultMissionNameStructure(formMission.name) && updatedMission.mode && updatedMission.repeat) {
      updatedMission.name = getDefaultMissionName(updatedMission.mode, roomMissions, updatedMission.repeat, mission.id);
    }
    setFormMission((prevState) => ({ ...prevState, ...updatedMission }));
  };

  const saveClickHandler = () => {
    if (mission.id == null) {
      dispatch(postNewMission(formMission));
    } else {
      dispatch(
        patchMission({
          id: formMission.id,
          data: formMission,
        })
      );
    }
  };

  const isSaveButtonActive =
    validateName(formMission.name).every((vR) => vR.isValid) &&
    formMission.repeat >= -1 &&
    formMission.mode.length > 0 &&
    ((formMission.mode === NavigatorName.ZoneCleaning &&
      formMission.selectedZoneIds != null &&
      formMission.selectedZoneIds.length > 0) ||
      (formMission.mode === NavigatorName.Maintenance &&
        formMission.selectedLandmarkIds != null &&
        formMission.selectedLandmarkIds.length > 0) ||
      (formMission.mode !== NavigatorName.ZoneCleaning && formMission.mode !== NavigatorName.Maintenance));

  return (
    <React.Fragment>
      <DialogWrapperWithClose
        open={open}
        onClose={closeDialogHandler}
        fullWidth
        maxWidth="md"
        title={mission.id == null ? t("mission-create-mission-title") : t("mission-edit-mission-title")}
        PaperProps={{
          sx: {
            transition: "min-height 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          },
        }}
      >
        <MissionFormContent
          formMission={formMission}
          onMissionChange={updateMissionHandler}
          roomId={roomId}
          navigatorAsName={navigatorAsName}
          includePadding
          bottomButtons={
            <ReadOnlyProtectedButton
              variant="contained"
              color="primary"
              size="small"
              startIcon={<SaveIcon />}
              onClick={saveClickHandler}
              disabled={!isSaveButtonActive}
            >
              {t("button-save")}
            </ReadOnlyProtectedButton>
          }
        />
      </DialogWrapperWithClose>
    </React.Fragment>
  );
};

export default MissionForm;